"use client"

import Script from "next/script"
import { FormEvent, useEffect, useState } from "react"

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""

type SubmitState = "idle" | "submitting" | "success" | "error"

export function SalesInterestForm() {
  const [siteKey, setSiteKey] = useState(RECAPTCHA_SITE_KEY)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [submitState, setSubmitState] = useState<SubmitState>("idle")
  const [message, setMessage] = useState("")

  useEffect(() => {
    if (siteKey) return
    let cancelled = false
    fetch("/api/recaptcha/config/", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: { site_key?: string } | null) => {
        const runtimeSiteKey = String(payload?.site_key || "").trim()
        if (!cancelled && runtimeSiteKey) {
          setSiteKey(runtimeSiteKey)
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [siteKey])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formElement = event.currentTarget
    setSubmitState("submitting")
    setMessage("")

    if (!siteKey || !window.grecaptcha || !recaptchaReady) {
      setSubmitState("error")
      setMessage("Contact form protection is not ready. Please try again in a moment.")
      return
    }

    const form = new FormData(formElement)
    try {
      const recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "contact" })
      const response = await fetch("/api/sales-interest/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: String(form.get("full_name") || ""),
          email: String(form.get("email") || ""),
          company: String(form.get("company") || ""),
          role: String(form.get("role") || ""),
          message: String(form.get("message") || ""),
          recaptcha_token: recaptchaToken,
        }),
      })

      if (!response.ok) {
        throw new Error("Request failed")
      }

      formElement.reset()
      setSubmitState("success")
      setMessage("Thanks. We have your request and will be in touch shortly.")
    } catch {
      setSubmitState("error")
      setMessage("We could not submit your request. Please try again.")
    }
  }

  return (
    <>
      {siteKey ? (
        <Script
          id="tiberius-recaptcha-api"
          src={`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`}
          strategy="afterInteractive"
          onLoad={() => window.grecaptcha?.ready(() => setRecaptchaReady(true))}
        />
      ) : null}
      <form onSubmit={handleSubmit} className="grid w-full gap-4 rounded-lg border border-white/15 bg-white/[0.06] p-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm font-semibold text-white">
          Name
          <input
            name="full_name"
            required
            autoComplete="name"
            className="h-12 rounded-lg border border-white/20 bg-white px-3 text-sm text-[#0A2E5C] outline-none transition placeholder:text-[#53677F] focus:border-[#93C5FD]"
            placeholder="Jane Client"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm font-semibold text-white">
          Work email
          <input
            name="email"
            type="email"
            required
            autoComplete="email"
            className="h-12 rounded-lg border border-white/20 bg-white px-3 text-sm text-[#0A2E5C] outline-none transition placeholder:text-[#53677F] focus:border-[#93C5FD]"
            placeholder="jane@example.com"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm font-semibold text-white">
          Company
          <input
            name="company"
            required
            autoComplete="organization"
            className="h-12 rounded-lg border border-white/20 bg-white px-3 text-sm text-[#0A2E5C] outline-none transition placeholder:text-[#53677F] focus:border-[#93C5FD]"
            placeholder="Example Ltd"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm font-semibold text-white">
          Role
          <input
            name="role"
            autoComplete="organization-title"
            className="h-12 rounded-lg border border-white/20 bg-white px-3 text-sm text-[#0A2E5C] outline-none transition placeholder:text-[#53677F] focus:border-[#93C5FD]"
            placeholder="Security lead"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm font-semibold text-white sm:col-span-2">
          Message
          <textarea
            name="message"
            rows={4}
            className="min-h-[112px] rounded-lg border border-white/20 bg-white px-3 py-3 text-sm text-[#0A2E5C] outline-none transition placeholder:text-[#53677F] focus:border-[#93C5FD]"
            placeholder="Tell us what you want to monitor."
          />
        </label>
        <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="submit"
            disabled={submitState === "submitting"}
            className="inline-flex h-[50px] items-center justify-center rounded-lg bg-white px-[22px] text-[15px] font-bold text-[#0A2E5C] transition hover:bg-[#F4F8FC] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitState === "submitting" ? "Submitting..." : "Register interest"}
          </button>
          {message ? (
            <p className={`text-sm font-semibold leading-5 ${submitState === "success" ? "text-[#A7F3D0]" : "text-[#FDE68A]"}`}>
              {message}
            </p>
          ) : null}
        </div>
      </form>
    </>
  )
}
